// What a host hands the chat views (ADR 0021, the -react shape): the SDK's client, where the chat service
// answers, the person's token, and the host's own affordances as slots. A platform host passes its viz sandbox, its
// chart windows, its page routes, its Board/query refresh, its agents roster, its fleet model names, its
// dashboards and its companion speed dial; a project's standalone app passes what it has. The views never
// import a host.
import type { ChatClient } from '@cortex/sdk-chat-client';
import * as React from 'react';

/** A custom visualization (show_viz): the host's sandbox draws it. */
export interface VizSlotProps {
  title: string;
  code: string;
  data: unknown;
  height?: number;
  testid: string;
  /** The person asks the model to fix a broken visualization (the chat sends the error back). */
  onFix?: (message: string) => void;
}

/** A chart or visualization opened as a floating window (the host's window manager). */
export interface ChartWindowRequest {
  title: string;
  content:
    | { kind: 'viz'; code: string; data: unknown }
    | { kind: 'chart'; svg?: string; columns: string[]; rows: Record<string, unknown>[] };
}

/** A project agent the chat can be switched to (the 🧩 Agents menu). */
export interface ChatAgentSummary {
  key: string;
  title: string;
  icon?: string | null;
  description?: string | null;
  /** What the agent covers (an app it backs: `{ kind: 'app', key }` offers "Open app →"). */
  covers?: { kind: string; key: string }[];
}

export interface ChatHost {
  /** The chat's client for the project a view is on (where the host mounts that project's chat). */
  clientOf: (slug: string) => ChatClient;
  /** The chat service's base URL (`/assistant` on the host's origin). */
  assistantBaseUrl: string;
  /**
   * The fields the host's turn route takes besides the chat's own (the app instance the chat is for, where the host
   * serves several); none: the host serves one.
   */
  turnFields?: Readonly<Record<string, string>>;
  /** What the host shows beside the project in the chat's header and status bar (its name of the instance); none. */
  context?: string;
  /** The person's bearer token (the host's session). */
  getToken: () => string | undefined;
  /** The signed-in person's email (the report names its author), when the host knows it. */
  userEmail?: string | null;
  /** The chat's name (the window's title, its hello and its input's hint); default `Assistant`. */
  brand?: string;
  /**
   * Subscribes to a change of an app's viewer options (its chat kind, its chat UI): `run` is called on each;
   * answers the unsubscribe. Absent: read once per open.
   */
  onCompanionsChanged?: (slug: string, app: string | null, run: () => void) => () => void;
  /** The host's simple view (Standard mode: the model bar, the chats rail, auto-open). */
  simple?: boolean;
  /** Draws a show_viz visualization; absent: the code is shown as text. */
  renderViz?: (p: VizSlotProps) => React.ReactNode;
  /** Opens a chart in a window of its own; absent: no "Open in window". */
  openChartWindow?: (o: ChartWindowRequest) => void;
  /** Routes the host app in place (in-app links, `navigate` blocks, the apps menu). */
  navigate: (path: string) => void;
  /** The host path of a page a `navigate` block names (`data-sources/3`), or null when it is unknown. */
  pagePath?: (raw: unknown, slug: string) => string | null;
  /** The host path of an app of the project. */
  appPath?: (app: string, slug: string) => string;
  /** A tool of a turn ended done: the host refreshes what it changed. */
  onToolDone?: (tool: string, slug: string) => void;
  /**
   * Records the features a person picked in the chat's checklist (the host's own record of a change request:
   * e.g. a Requirements source); a failure is shown in the chat. Absent: the selection is only sent as the next
   * message.
   */
  recordFeatures?: (slug: string, r: { title: string; text: string; sessionId?: string }) => Promise<void>;
  /** The project agents the person may switch the chat to. */
  listAgents?: (slug: string) => Promise<ChatAgentSummary[]>;
  /** Friendly names of model ids (the model picker's labels). */
  modelLabels?: () => Promise<Map<string, string>>;
  /** Builds a live dashboard from a report's charts that carry their SQL; answers its page path. */
  generateDashboard?: (
    slug: string,
    r: { title: string; charts: Record<string, unknown>[] },
  ) => Promise<string>;
  /** The path of a saved report's own page (the popup); absolute URLs are built on the origin. */
  reportPath: (slug: string, id: string) => string;
  /** WHICH chat an app's Chat companion opens (its declaration's chat kind). */
  chatKindOf?: (slug: string, app: string) => Promise<'assistant' | 'analyst'>;
  /**
   * WHO draws an app's chat (its declaration's `chat_ui`): 'host' — this chat window — or 'app', the app's
   * own chat UI on the headless chat (ADR 0021 amendment of 2026-10-08): then no chat window is offered over
   * that app and the dial has no Chat. Absent: 'host'.
   */
  chatUiOf?: (slug: string, app: string) => Promise<'host' | 'app'>;
  /** The app's companion speed dial that replaces the launcher while an app is open. */
  dial?: (p: { slug: string; app: string; hidden: boolean }) => React.ReactNode;
}

const Ctx = React.createContext<ChatHost | null>(null);

/** Mounts the chat views: every view below reads the client and slots from it. */
export function ChatProvider({ value, children }: { value: ChatHost; children: React.ReactNode }) {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useChatHost(): ChatHost {
  const v = React.useContext(Ctx);
  if (!v) throw new Error('Chat views need a <ChatProvider> (the SDK’s client and slots)');
  return v;
}
